import React, { useState, useEffect, useContext } from "react";
import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../context/AuthContext";
import { SubscriptionContext } from "../context/SubscriptionContext";
import { Menu, BarChart3, Users, Clock, AlertCircle, FileBarChart, RefreshCw, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import api from "../utils/Api";

const bottomItems = [
  { path: "/admin/dashboard",   label: "Dashboard",   icon: <BarChart3 size={20} /> },
  { path: "/admin/empleados",   label: "Empleados",   icon: <Users size={20} /> },
  { path: "/admin/fichajes",    label: "Fichajes",    icon: <Clock size={20} /> },
  { path: "/admin/incidencias", label: "Incidencias", icon: <AlertCircle size={20} /> },
  { path: "/admin/informes",    label: "Informes",    icon: <FileBarChart size={20} /> },
];

// Rutas siempre accesibles aunque no haya suscripción
const SUBSCRIPTION_FREE_PATHS = ["/admin/subscribe"];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useContext(AuthContext);
  const { subscribed, loading: loadingSub } = useContext(SubscriptionContext);
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
  }, [sidebarOpen]);

  const handleRecalcular = async () => {
    try { await api.post("/recalcular-horas"); } catch { /* silent */ }
  };

  const initial = (user?.rol || "A").charAt(0).toUpperCase();

  // ── Mientras cargamos el estado de suscripción, mostramos spinner ──────────
  if (loadingSub) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 14, color: "var(--clr-muted)" }}>
        <RefreshCw size={28} style={{ animation: "spin 1s linear infinite", color: "var(--clr-accent)" }} />
        <span style={{ fontSize: 14 }}>Cargando…</span>
      </div>
    );
  }

  // ── Si la suscripción ha expirado y el usuario no está ya en /admin/subscribe,
  //    redirigimos inmediatamente. La propia ruta subscribe siempre es libre. ──
  const isFreeRoute = SUBSCRIPTION_FREE_PATHS.some(p => location.pathname.startsWith(p));
  if (subscribed === false && !isFreeRoute) {
    return <Navigate to="/admin/subscribe" replace />;
  }

  return (
    <div className={`tn-shell${sidebarCollapsed ? " tn-shell--collapsed" : ""}`}>
      {/* Sidebar desktop + drawer mobile */}
      {sidebarOpen && (
        <div className="tn-overlay d-lg-none" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar
        onClose={() => setSidebarOpen(false)}
        subscribed={subscribed}
        isOpen={sidebarOpen}
      />

      {/* Main area */}
      <div className={`tn-main${sidebarOpen ? " open" : ""}`} style={{ width: "100%" }}>
        {/* Topbar */}
        <header className="tn-topbar">
          <button className="tn-topbar__hamburger d-lg-none" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú">
            <Menu size={22} />
          </button>
          <button
            className="tn-topbar__hamburger d-none d-lg-flex"
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label={sidebarCollapsed ? "Mostrar menú" : "Ocultar menú"}
            title={sidebarCollapsed ? "Mostrar menú" : "Ocultar menú"}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>

          <div className="d-flex align-items-center gap-2 d-lg-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" className="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
            <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--clr-text)", letterSpacing: "-0.3px" }}>Turnora</span>
          </div>

          <div className="ms-auto d-flex align-items-center gap-3">
            <span style={{ fontSize: 13, color: "var(--clr-muted)", display: "none" }} className="d-md-inline">Administrador</span>
            <button
              className="tn-avatar"
              title="Recalcular horas"
              onClick={handleRecalcular}
              style={{ border: "none", cursor: "pointer" }}
            >
              {initial}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="tn-page">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav (mobile) — solo si hay suscripción activa */}
      <nav className="tn-bottom-nav">
        {subscribed ? (
          bottomItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `tn-bottom-nav__item ${isActive ? "active" : ""}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))
        ) : (
          // Sin suscripción: solo el acceso a la pantalla de suscripción
          <NavLink to="/admin/subscribe" className={({ isActive }) => `tn-bottom-nav__item ${isActive ? "active" : ""}`}>
            <AlertCircle size={20} />
            <span>Suscripción</span>
          </NavLink>
        )}
      </nav>
    </div>
  );
}
